import React from 'react';
import { Dialog, DialogContent, Box, Typography, Button, IconButton } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { Theme } from '@material-ui/core/styles';
import { Close, EventNote, TouchApp, Notifications, Assignment, CameraAlt, Chat } from '@material-ui/icons';

const APP_STORE_URL = 'https://apps.apple.com/au/app/volaby/id6754815765';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.Volaby&hl=en';

const APP_STORE_BADGE =
  'https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83';
const PLAY_STORE_BADGE = 'https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png';

const FEATURES = [
  {
    icon: EventNote,
    label: 'Roster on the Go',
    description:
      'View upcoming rosters, events and commitments with instant change notifications. Search opportunities and set availability directly from the app.',
    bg: 'rgba(191, 227, 214, 0.4)',
    color: '#0A4439',
  },
  {
    icon: TouchApp,
    label: 'Check-In & Attendance',
    description:
      'Mark attendance with a single tap. Complete session reports and track hours automatically, no paperwork required.',
    bg: 'rgba(250, 211, 228, 0.45)',
    color: '#6E1C42',
  },
  {
    icon: Notifications,
    label: 'Stay Updated',
    description:
      'Push notifications for schedule changes, new opportunities and announcements keep your team informed in real time.',
    bg: 'rgba(251, 233, 210, 0.45)',
    color: '#6E1C42',
  },
  {
    icon: Assignment,
    label: 'Complete Tasks Anywhere',
    description:
      'Access onboarding tasks, training modules and forms on mobile. Upload documents and sign forms without needing a desktop.',
    bg: 'rgba(149, 220, 220, 0.45)',
    color: '#0B627F',
  },
  {
    icon: CameraAlt,
    label: 'Incident Reporting',
    description:
      'Report incidents on the spot with photo uploads and custom actions. Managers receive instant alerts to respond quickly.',
    bg: 'rgba(250, 211, 228, 0.45)',
    color: '#6E1C42',
  },
  {
    icon: Chat,
    label: 'Team Communication',
    description:
      'Direct team messaging and engagement features keep everyone connected, whether coordinating a shift or sharing updates.',
    bg: 'rgba(191, 227, 214, 0.4)',
    color: '#0A4439',
  },
];

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogPaper: {
      borderRadius: 8,
      maxWidth: 640,
    },
    header: {
      padding: '24px 24px 0 24px',
      position: 'relative',
    },
    closeButton: {
      position: 'absolute',
      top: 16,
      right: 16,
      color: theme.palette.text.secondary,
    },
    content: {
      padding: '16px 24px 32px 24px',
    },
    subtitle: {
      color: theme.palette.text.secondary,
      fontSize: 14,
      marginTop: 4,
      lineHeight: 1.5,
    },
    featuresList: {
      display: 'flex',
      flexDirection: 'column',
      marginTop: 8,
    },
    featureCard: {
      borderRadius: 10,
      padding: '14px 16px',
      marginBottom: 16,
      '&:last-child': {
        marginBottom: 0,
      },
    },
    featureHeader: {
      display: 'flex',
      alignItems: 'center',
    },
    featureIcon: {
      fontSize: 18,
      flexShrink: 0,
      marginRight: 8,
    },
    featureLabel: {
      fontFamily: '"Outfit", "Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: 15,
      fontWeight: 600,
    },
    featureDescription: {
      fontFamily: '"Outfit", "Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: 13.5,
      fontWeight: 400,
      lineHeight: 1.35,
      paddingTop: 6,
      paddingLeft: 6,
      paddingRight: 6,
    },
    badgesContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(3),
    },
    storeBadge: {
      cursor: 'pointer',
      transition: 'opacity 0.2s ease',
      '&:hover': {
        opacity: 0.75,
      },
    },
    appStoreBadge: {
      height: 52,
    },
    playStoreBadge: {
      height: 76,
    },
    doneContainer: {
      marginTop: 24,
    },
    doneButton: {
      backgroundColor: '#796fff',
      color: '#ffffff',
      textTransform: 'none',
      fontWeight: 600,
      fontSize: 14,
      padding: '8px 24px',
      borderRadius: 6,
      '&:hover': {
        backgroundColor: '#635ac7',
      },
    },
  })
);

interface DownloadAppDialogProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
}

export function DownloadAppDialog({ open, onClose, onComplete }: DownloadAppDialogProps) {
  const classes = useStyles();

  const handleLinkClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth classes={{ paper: classes.dialogPaper }}>
      <Box className={classes.header}>
        <IconButton className={classes.closeButton} onClick={onClose} size='small'>
          <Close fontSize='small' />
        </IconButton>
        <Typography style={{ fontWeight: 700, fontSize: 20, letterSpacing: '-0.02em' }}>Download the App</Typography>
        <Typography className={classes.subtitle}>
          Manage your volunteer operations anywhere, anytime. The Volaby app gives your team the tools they need to stay
          connected and manage rosters on the go.
        </Typography>
      </Box>

      <DialogContent className={classes.content}>
        <Box className={classes.badgesContainer}>
          <img
            src={APP_STORE_BADGE}
            alt='Download on the App Store'
            className={`${classes.storeBadge} ${classes.appStoreBadge}`}
            onClick={() => handleLinkClick(APP_STORE_URL)}
          />
          <img
            src={PLAY_STORE_BADGE}
            alt='Get it on Google Play'
            className={`${classes.storeBadge} ${classes.playStoreBadge}`}
            onClick={() => handleLinkClick(PLAY_STORE_URL)}
          />
        </Box>

        <Box className={classes.featuresList}>
          {FEATURES.map((feature) => (
            <Box
              key={feature.label}
              className={classes.featureCard}
              style={{ backgroundColor: feature.bg, color: feature.color }}
            >
              <Box className={classes.featureHeader}>
                <feature.icon className={classes.featureIcon} />
                <Typography className={classes.featureLabel} style={{ color: 'inherit' }}>
                  {feature.label}
                </Typography>
              </Box>
              <Typography className={classes.featureDescription} style={{ color: 'inherit' }}>
                {feature.description}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box display='flex' justifyContent='flex-end' className={classes.doneContainer}>
          <Button className={classes.doneButton} onClick={onComplete}>
            Done
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
